import {
  Alert,
  Box,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  CallContact,
  CallEndReason,
  CallLogEntry,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import TableSkeleton from "./TableSkeleton";

/**
 * Care → Video calls · Call log (VC-25).
 *
 * The read-only answer to "who looked into room 214 on Tuesday?" - a family
 * member, a care manager or a regulator will ask it, and this table is where it
 * is answered. It is READ-ONLY on purpose: it renders the audit trail, and an
 * audit trail you can edit is not evidence. There is no add, no delete, no
 * inline edit anywhere on this surface.
 *
 * It shows THAT a call happened - when, which room, which way, who, how long,
 * answered or not - and never WHAT was said. There is no column for content
 * because there is no content anywhere in the system to put in one (VC-16); the
 * rows are projected from audit events that never held a recording.
 *
 * Filters mirror the three questions the log is actually asked: which room,
 * which day(s), which contact. Each change refetches server-side, so the client
 * never holds more of the trail than it is showing.
 */

interface Props {
  premiseId: string;
}

/** How each end reason reads to a care manager, and the chip colour it earns. */
const REASON_LABEL: Record<
  CallEndReason,
  { label: string; color: "success" | "default" | "warning" | "error" }
> = {
  answered_then_hangup: { label: "Answered", color: "success" },
  declined: { label: "Declined", color: "default" },
  timeout: { label: "No answer", color: "warning" },
  busy: { label: "Busy", color: "warning" },
  policy_block: { label: "Ended by policy", color: "default" },
  error: { label: "Failed", color: "error" },
};

/** Seconds → "3m 04s" / "48s" / "-". A call that never connected reads "-". */
function formatDuration(entry: CallLogEntry): string {
  if (!entry.answered || entry.durationSec <= 0) return "-";
  const m = Math.floor(entry.durationSec / 60);
  const s = entry.durationSec % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
}

/** ISO → a compact, locale-aware "when". */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Who placed the call, as far as the log can name them without disclosing PHI. */
function initiatorLabel(
  entry: CallLogEntry,
  contactsById: Map<string, CallContact>
): string {
  const { kind, contactId, userId } = entry.initiator;
  if (contactId) {
    const c = contactsById.get(contactId);
    if (c) return `${c.displayName}${c.relation ? ` (${c.relation})` : ""}`;
    return "Directory contact";
  }
  if (userId) return "Staff";
  if (kind === "room") return "Room";
  return kind === "staff" ? "Staff" : "Phone";
}

export default function CallLogViewer({ premiseId }: Props) {
  const [entries, setEntries] = useState<CallLogEntry[] | null>(null);
  const [contacts, setContacts] = useState<CallContact[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [roomId, setRoomId] = useState("");
  const [contactId, setContactId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const contactsById = useMemo(
    () => new Map(contacts.map((c) => [c.id, c])),
    [contacts]
  );

  // The contact filter offers every contact the premise has ever had, revoked
  // included: a call from someone since removed still needs to be findable in
  // the log - that history does not disappear when the contact does.
  useEffect(() => {
    let alive = true;
    foyerApi
      .listCallContacts(premiseId, { includeRevoked: true })
      .then((list) => alive && setContacts(list))
      .catch(() => alive && setContacts([]));
    return () => {
      alive = false;
    };
  }, [premiseId]);

  const load = useCallback(async () => {
    setError(null);
    setEntries(null);
    try {
      // `to` is a date; make it inclusive of the whole day by pushing the
      // exclusive upper bound to the following midnight.
      const toExclusive = to ? isoEndOfDay(to) : undefined;
      const page = await foyerApi.listCallLog(premiseId, {
        roomId: roomId.trim() || undefined,
        contactId: contactId || undefined,
        from: from || undefined,
        to: toExclusive,
      });
      setEntries(page.entries);
      setTruncated(page.truncated);
    } catch {
      setError("Could not load the call log. Try narrowing the date range.");
      setEntries([]);
    }
  }, [premiseId, roomId, contactId, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Stack spacing={2} data-testid="call-log">
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
        Every call this premise has placed or received - who, when, which way
        and how long. Read-only, and it never records what was said.
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        useFlexGap
        flexWrap="wrap"
      >
        <TextField
          label="Room"
          value={roomId}
          onChange={(e) => setRoomId(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
          inputProps={{ "data-testid": "call-log-room" }}
        />
        <TextField
          select
          label="Contact"
          value={contactId}
          onChange={(e) => setContactId(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          SelectProps={{ native: false }}
          inputProps={{ "data-testid": "call-log-contact" }}
        >
          <MenuItem value="">All contacts</MenuItem>
          {contacts.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.displayName}
              {c.relation ? ` (${c.relation})` : ""}
              {c.revokedAt ? " · removed" : ""}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="From"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "call-log-from" }}
        />
        <TextField
          label="To"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "call-log-to" }}
        />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {truncated && (
        <Alert severity="info" data-testid="call-log-truncated">
          Showing the most recent calls only - narrow the date range to see the
          rest.
        </Alert>
      )}

      {entries === null ? (
        <TableSkeleton rows={5} />
      ) : entries.length === 0 ? (
        <Alert severity="info" data-testid="call-log-empty">
          No calls match these filters.
        </Alert>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" data-testid="call-log-table">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Room</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Outcome</TableCell>
                <TableCell align="right">Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => {
                const reason = REASON_LABEL[entry.reason] ?? {
                  label: entry.reason,
                  color: "default" as const,
                };
                const outbound = entry.direction === "outbound";
                return (
                  <TableRow key={entry.seq} data-testid="call-log-row">
                    <TableCell>{formatWhen(entry.at)}</TableCell>
                    <TableCell>{entry.roomId || "-"}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          outbound
                            ? "The room called out"
                            : "Someone called the room"
                        }
                      >
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          {outbound ? (
                            <CallMadeIcon fontSize="small" color="action" />
                          ) : (
                            <CallReceivedIcon fontSize="small" color="action" />
                          )}
                          <span>{outbound ? "Outbound" : "Inbound"}</span>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {initiatorLabel(entry, contactsById)}
                      {entry.autoAnswer && (
                        <Chip
                          label="Auto"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1 }}
                          data-testid="call-log-auto"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={reason.label}
                        size="small"
                        color={reason.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{formatDuration(entry)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  );
}

/** A YYYY-MM-DD date string → the ISO instant at the START of the next day, so
 *  an inclusive "to yesterday" covers all of yesterday. */
function isoEndOfDay(date: string): string {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}
